import React from 'react';
import { useChartColors } from '@/hooks/useChartColors';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line, Bar, Pie, Doughnut } from 'react-chartjs-2';
import { Card, CardContent, CardHeader, CardTitle } from './card';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

/**
 * Build Chart.js options with theme-reactive colors.
 * Call this with the result of useChartColors() so that chart options
 * update whenever the user switches themes or Tweakcn changes CSS vars.
 */
export function buildChartOptions(colors) {
  return {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          font: {
            family: 'Urbanist, sans-serif',
            size: 12
          },
          color: colors.foreground,
          padding: 15
        }
      },
      tooltip: {
        backgroundColor: colors.foreground,
        titleColor: colors.background,
        bodyColor: colors.background,
        borderColor: colors.chart2,
        borderWidth: 1,
        padding: 12,
        boxPadding: 6,
        usePointStyle: true,
        font: {
          family: 'Urbanist, sans-serif',
          size: 12
        }
      }
    },
    scales: {
      x: {
        grid: {
          display: false
        },
        ticks: {
          color: colors.foreground,
          font: {
            family: 'Urbanist, sans-serif',
            size: 11
          }
        }
      },
      y: {
        grid: {
          color: colors.muted,
          borderDash: [5, 5]
        },
        ticks: {
          color: colors.foreground,
          font: {
            family: 'Urbanist, sans-serif',
            size: 11
          }
        }
      }
    }
  };
}

/**
 * Generate a color palette array for multiple datasets.
 * Accepts an array of color values (e.g. from useChartColors).
 */
export const generateColorPalette = (count, paletteColors) => {
  const defaults = ['#313131', '#f19f57', '#ff8b6c', '#44b672', '#3b82f6', '#8b5cf6', '#ec4899'];
  const source = paletteColors && paletteColors.length ? paletteColors : defaults;
  return Array.from({ length: count }, (_, i) => source[i % source.length]);
};

/**
 * Line Chart Wrapper
 */
export function LineChart({ data, options = {}, title, height = 300, className = '' }) {
  const colors = useChartColors();
  const defaultOptions = buildChartOptions(colors);
  const mergedOptions = {
    ...defaultOptions,
    ...options,
    plugins: {
      ...defaultOptions.plugins,
      ...options.plugins
    }
  };

  const chartContent = (
    <div style={{ height }}>
      <Line data={data} options={mergedOptions} />
    </div>
  );

  // Only wrap in Card when a title is provided (standalone usage)
  // When used inside widgets, no title is passed so Card wrapper is skipped
  if (!title) return chartContent;

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="text-primary">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        {chartContent}
      </CardContent>
    </Card>
  );
}

/**
 * Bar Chart Wrapper
 */
export function BarChart({ data, options = {}, title, height = 300, horizontal = false, className = '' }) {
  const colors = useChartColors();
  const defaultOptions = buildChartOptions(colors);
  const mergedOptions = {
    ...defaultOptions,
    indexAxis: horizontal ? 'y' : 'x',
    ...options,
    plugins: {
      ...defaultOptions.plugins,
      ...options.plugins
    }
  };

  const chartContent = (
    <div style={{ height }}>
      <Bar data={data} options={mergedOptions} />
    </div>
  );

  // Only wrap in Card when a title is provided (standalone usage)
  if (!title) return chartContent;

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="text-primary">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        {chartContent}
      </CardContent>
    </Card>
  );
}

/**
 * Pie Chart Wrapper
 */
export function PieChart({ data, options = {}, title, height = 300, className = '' }) {
  const colors = useChartColors();
  const defaultOptions = buildChartOptions(colors);
  const mergedOptions = {
    ...defaultOptions,
    ...options,
    responsive: true,
    maintainAspectRatio: false,
    // Pie charts have no axes — override the defaultOptions scales entirely
    scales: {},
    // Show pointer cursor when hovering over a slice; restore default when not
    onHover: options.onHover ?? ((event, elements) => {
      if (event?.native?.target) {
        event.native.target.style.cursor = elements.length > 0 ? 'pointer' : 'default';
      }
    }),
    plugins: {
      ...defaultOptions.plugins,
      ...options.plugins,
      legend: {
        display: false
      }
    }
  };

  const chartContent = (
    <div style={{ height }}>
      <Pie data={data} options={mergedOptions} />
    </div>
  );

  // Only wrap in Card when a title is provided (standalone usage)
  if (!title) return chartContent;

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="text-primary">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        {chartContent}
      </CardContent>
    </Card>
  );
}

/**
 * Doughnut Chart Wrapper
 */
export function DoughnutChart({ data, options = {}, title, height = 300, className = '' }) {
  const colors = useChartColors();
  const defaultOptions = buildChartOptions(colors);
  const mergedOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      ...defaultOptions.plugins,
      ...options.plugins,
      legend: {
        ...defaultOptions.plugins.legend,
        position: 'right'
      }
    },
    cutout: '60%'
  };

  const chartContent = (
    <div style={{ height }}>
      <Doughnut data={data} options={mergedOptions} />
    </div>
  );

  // Only wrap in Card when a title is provided (standalone usage)
  if (!title) return chartContent;

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="text-primary">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        {chartContent}
      </CardContent>
    </Card>
  );
}

/**
 * Multi-line Chart (for comparisons)
 */
export function MultiLineChart({ datasets, labels, options = {}, title, height = 300, className = '' }) {
  const themeColors = useChartColors();
  const palette = [themeColors.chart1, themeColors.chart2, themeColors.chart3, themeColors.chart5];
  const colors = generateColorPalette(datasets.length, palette);
  
  const data = {
    labels,
    datasets: datasets.map((dataset, index) => ({
      ...dataset,
      borderColor: dataset.borderColor || colors[index],
      backgroundColor: dataset.backgroundColor || `${colors[index]}33`, // 20% opacity
      tension: 0.4,
      fill: dataset.fill !== undefined ? dataset.fill : false
    }))
  };

  return <LineChart data={data} options={options} title={title} height={height} className={className} />;
}

/**
 * Stacked Bar Chart
 */
export function StackedBarChart({ datasets, labels, options = {}, title, height = 300, horizontal = false, className = '' }) {
  const themeColors = useChartColors();
  const palette = [themeColors.chart1, themeColors.chart2, themeColors.chart3, themeColors.chart5];
  const colors = generateColorPalette(datasets.length, palette);
  
  const data = {
    labels,
    datasets: datasets.map((dataset, index) => ({
      ...dataset,
      backgroundColor: dataset.backgroundColor || colors[index],
      borderColor: dataset.borderColor || colors[index],
      borderWidth: 1
    }))
  };

  const mergedOptions = {
    ...options,
    scales: {
      ...options.scales,
      x: {
        ...options.scales?.x,
        stacked: true
      },
      y: {
        ...options.scales?.y,
        stacked: true
      }
    }
  };

  return <BarChart data={data} options={mergedOptions} title={title} height={height} horizontal={horizontal} className={className} />;
}

/**
 * Simple metric display (for single values)
 */
export function MetricCard({ label, value, change, changeType = 'neutral', icon: Icon, className = '' }) {
  const changeColor = changeType === 'positive' ? 'text-green-600' : 
                     changeType === 'negative' ? 'text-destructive' : 
                     'text-primary/70';

  return (
    <Card className={className}>
      <CardContent className="pt-6">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <p className="text-sm font-medium text-primary/70">{label}</p>
            <p className="text-2xl font-bold text-primary mt-1">{value}</p>
            {change && (
              <p className={`text-sm mt-1 ${changeColor}`}>
                {change}
              </p>
            )}
          </div>
          {Icon && (
            <div className="ml-4">
              <Icon className="h-8 w-8 text-accent" />
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default {
  LineChart,
  BarChart,
  PieChart,
  DoughnutChart,
  MultiLineChart,
  StackedBarChart,
  MetricCard,
  buildChartOptions,
  generateColorPalette
};

